import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { photoTransferQueue } from "../queue/photoTransferQueue";
import { PhotoStatus } from "../utils/constants";

const PHOTO_COMPARE_KEYS = [
  "key",
  "status",
  "subStatus",
  "progress",
  "statusMessage",
  "isPaused",
  "error",
  "id",
];

const arePhotosEqual = (prev, next) => {
  if (prev.length !== next.length) return false;
  return next.every((photo, i) => {
    const prevPhoto = prev[i];
    if (!prevPhoto) return false;
    return PHOTO_COMPARE_KEYS.every((k) => prevPhoto[k] === photo[k]);
  });
};

const useStore =
  React.useSyncExternalStore ||
  ((subscribe, getSnapshot) => {
    const [state, setState] = useState(getSnapshot);
    useEffect(
      () => subscribe(() => setState(getSnapshot())),
      [subscribe, getSnapshot],
    );
    return state;
  });

const PhotoTransferContext = createContext(null);

export const PhotoTransferProvider = ({ children }) => {
  const snapshot = useStore(
    photoTransferQueue.subscribe,
    photoTransferQueue.getSnapshot,
  );

  useEffect(() => {
    photoTransferQueue.init();
    return () => photoTransferQueue.destroy();
  }, []);

  const value = useMemo(() => ({ ...snapshot }), [snapshot]);

  return (
    <PhotoTransferContext.Provider value={value}>
      {children}
    </PhotoTransferContext.Provider>
  );
};

const useContextState = () => {
  const context = useContext(PhotoTransferContext);
  if (!context) {
    throw new Error(
      "usePhotoTransfers must be used within PhotoTransferProvider",
    );
  }
  return context;
};

const sortPhotosByDateDesc = (photos) => {
  if (!photos || photos.length === 0) return [];
  return [...photos].sort((a, b) => {
    const tA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return tB - tA;
  });
};

export const usePhotoTransfers = (parentId) => {
  const context = useContextState();
  const [loadError, setLoadError] = useState(null);
  const prevPhotosRef = useRef([]);

  useEffect(() => {
    if (!parentId) {
      setLoadError(null);
      return undefined;
    }

    let isActive = true;
    setLoadError(null);
    photoTransferQueue.retainParent(parentId);
    photoTransferQueue.ensureParentLoaded(parentId).catch((err) => {
      if (isActive) {
        setLoadError(err?.message || "Failed to load photos");
      }
    });
    return () => {
      isActive = false;
      photoTransferQueue.releaseParent(parentId);
    };
  }, [parentId]);

  const photos = useMemo(() => {
    if (!parentId) {
      prevPhotosRef.current = [];
      return prevPhotosRef.current;
    }
    const parentPhotos = photoTransferQueue.getParentPhotos(parentId);
    const sorted = sortPhotosByDateDesc(parentPhotos);

    // Return stable reference if photos haven't meaningfully changed
    if (arePhotosEqual(prevPhotosRef.current, sorted)) {
      return prevPhotosRef.current;
    }

    prevPhotosRef.current = sorted;
    return sorted;
  }, [context.photos, parentId]);

  const activeUploads = useMemo(
    () =>
      photos.filter(
        (photo) => photo.status && photo.status !== PhotoStatus.DONE,
      ),
    [photos],
  );

  const queueSummary = useMemo(
    () => ({
      pendingUploadsSize: context.pendingUploadsSize,
      queueSize: context.queueSize,
      pendingDeletesSize: context.pendingDeletesSize,
    }),
    [context.pendingUploadsSize, context.queueSize, context.pendingDeletesSize],
  );

  return {
    photos,
    activeUploads,
    queueSummary,
    startUpload: photoTransferQueue.startUpload,
    requestDelete: photoTransferQueue.requestDelete,
    loadError,
  };
};

export const useGlobalTransfers = () => useContextState();
